import Env from '@/plugins/protokol/lib/Env'

/**
 * Streams a message to the Cortex API via SSE fetch.
 * Axios does not support ReadableStream, so we use fetch directly.
 *
 * @param {string} conversationUUID
 * @param {string} content
 * @param {string|null} agentUUID
 * @param {AbortSignal} abortSignal
 * @param {Function} onEvent  - called with { type, data } for each SSE event
 * @param {string[]} [images] - optional base64 data: URLs for vision
 * @param {Array} [tools] - optional tool schemas (OpenAI function format) to advertise to the LLM
 * @param {Array} [toolResults] - optional tool call results to submit back to the LLM
 * @param {Object|null} [pageContext] - optional page context from window.kortex.registerContext
 * @returns {Promise}
 */
export function streamMessage(conversationUUID, content, agentUUID, abortSignal, onEvent, images, tools, toolResults, pageContext) {
  var cortexHost = process.env.VUE_APP_KORTEX_HOST || Env.get('KORTEX_HOST') || (Env.get('API_HOST') + '/luma/kortex')
  var projectUUID = localStorage.getItem('current_project_uuid') || ''
  var projectEnv = localStorage.getItem('current_env') || 'live'
  var locale = localStorage.getItem('locale') || 'en_US'

  var headers = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
    'Accept-Language': locale,
  }

  if (projectUUID) {
    headers['X-Project-Uuid'] = projectUUID
  }

  if (projectEnv) {
    headers['X-Project-Env'] = projectEnv
  }

  var body = { content: content || '' }
  if (agentUUID) {
    body.agent_uuid = agentUUID
  }
  if (images && images.length > 0) {
    body.images = images
  }
  if (tools && tools.length > 0) {
    body.tools = tools
  }
  if (toolResults && toolResults.length > 0) {
    body.tool_results = toolResults
  }
  if (pageContext) {
    body.page_context = pageContext
  }

  return fetch(
    cortexHost + '/v1/conversations/' + conversationUUID + '/messages',
    {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify(body),
      signal: abortSignal
    }
  ).then(function (response) {
    if (!response.ok) {
      var status = response.status
      return response.json().then(function (data) {
        var msg = (data && (data.error || data.message)) || ('Request failed with status ' + status)
        var err = new Error(msg)
        err.status = status
        throw err
      }).catch(function (e) {
        if (e.status) throw e
        throw new Error('Request failed with status ' + status)
      })
    }

    var reader = response.body.getReader()
    var decoder = new TextDecoder()
    var buffer = ''

    function read() {
      return reader.read().then(function (result) {
        if (result.done) {
          onEvent({ type: 'done' })
          return
        }

        buffer += decoder.decode(result.value, { stream: true })
        var lines = buffer.split('\n')
        buffer = lines.pop()

        var eventType = ''
        for (var i = 0; i < lines.length; i++) {
          var line = lines[i]
          if (line.indexOf('event: ') === 0) {
            eventType = line.slice(7).trim()
          } else if (line.indexOf('data: ') === 0) {
            var rawData = line.slice(6)
            if (rawData === '[DONE]') {
              onEvent({ type: 'done' })
              return
            }
            try {
              var parsed = JSON.parse(rawData)
              onEvent({ type: eventType, data: parsed })
            } catch (e) {
              // ignore malformed lines
            }
          } else if (line === '') {
            eventType = ''
          }
        }

        return read()
      })
    }

    return read()
  })
}
